import type { AudioEngineInterface, SoundType } from './types.js';

// 生成无缝循环噪声 buffer。
// 两个关键点：
// 1) 循环长度足够长（默认 2s），避免短 buffer 反复播放被耳朵听成固定音高（嗡鸣）。
// 2) 在循环接缝做交叉淡化，消除首尾采样跳变导致的周期性哔剥声。
// fill(i) 返回第 i 个采样值；调用方负责噪声的具体算法（brown/white/pink 等）。
function makeLoopBuffer(
  ctx: BaseAudioContext,
  seconds: number,
  fill: (i: number) => number,
  crossfadeSec = 0.05
): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.floor(seconds * sr);
  const xf = Math.min(Math.floor(crossfadeSec * sr), Math.floor(n / 2));

  // 多生成 xf 个采样，用于把尾部平滑折回头部
  const raw = new Float32Array(n + xf);
  for (let i = 0; i < raw.length; i++) raw[i] = fill(i);

  const buffer = ctx.createBuffer(1, n, sr);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < n; i++) {
    if (i < xf) {
      // 头部 xf 个采样与"尾部延伸段"交叉淡化，保证 data[n-1] → data[0] 连续
      const w = i / xf;
      data[i] = raw[i] * w + raw[n + i] * (1 - w);
    } else {
      data[i] = raw[i];
    }
  }
  return buffer;
}

export const AudioEngine: AudioEngineInterface = {
  ctx: null,
  masterGain: null,
  nodes: [],
  soundGains: {} as Record<SoundType, GainNode>,

  init() {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);
    this.masterGain.gain.value = 0;

    this.createNoiseLayer(400, 'lowpass', 0.8, 0.02, 'rumble');
    this.createNoiseLayer(800, 'lowpass', 0.4, 0.08, 'rain');
    this.createNoiseLayer(1500, 'bandpass', 0.3, 0.05, 'wind');
    this.createNoiseLayer(3000, 'highpass', 0.1, 0.04, 'hiss');

    this.initAmbientLayers();
  },

  createNoiseLayer(freq: number, type: BiquadFilterType, vol: number, bufferScale: number, soundType: SoundType) {
    if (!this.ctx) return;

    // 用 2s 无缝循环 buffer 替代原来 0.02–0.08s 的短 buffer：
    // 短 buffer 循环会被听成固定音高（嗡鸣），且首尾跳变产生规律哔剥。
    // bufferScale 参数保留以兼容调用签名，但不再决定循环长度。
    void bufferScale;
    let lastOut = 0;
    const buffer = makeLoopBuffer(this.ctx, 2.0, () => {
      const white = Math.random() * 2 - 1;
      // highpass 层是纯白噪声；其余为 brown noise（积分器需保持跨采样状态）
      if (type === 'highpass') return white;
      lastOut = (lastOut + 0.02 * white) / 1.02;
      return lastOut * 3.5;
    });

    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;

    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;

    const gain = this.ctx.createGain();
    gain.gain.value = vol;

    const soundGain = this.ctx.createGain();
    soundGain.gain.value = 1.0;
    if (soundType) {
      this.soundGains[soundType] = soundGain;
    }

    // LFO 1
    const lfo = this.ctx.createOscillator();
    const waveTypes: OscillatorType[] = ['sine', 'triangle', 'sawtooth'];
    lfo.type = waveTypes[Math.floor(Math.random() * waveTypes.length)];
    lfo.frequency.value = 0.08 + Math.random() * 0.15;

    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = vol * (0.25 + Math.random() * 0.2);

    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);
    lfo.start();

    // LFO 2
    const lfo2 = this.ctx.createOscillator();
    lfo2.type = ['sine', 'triangle'][Math.floor(Math.random() * 2)] as OscillatorType;
    lfo2.frequency.value = 0.02 + Math.random() * 0.08;

    const lfo2Gain = this.ctx.createGain();
    lfo2Gain.gain.value = vol * (0.3 + Math.random() * 0.25);

    lfo2.connect(lfo2Gain);
    lfo2Gain.connect(gain.gain);
    lfo2.start();

    // LFO 3
    const lfo3 = this.ctx.createOscillator();
    lfo3.type = ['sine', 'sawtooth'][Math.floor(Math.random() * 2)] as OscillatorType;
    lfo3.frequency.value = 0.15 + Math.random() * 0.25;

    const lfo3Gain = this.ctx.createGain();
    lfo3Gain.gain.value = vol * (0.15 + Math.random() * 0.15);

    lfo3.connect(lfo3Gain);
    lfo3Gain.connect(gain.gain);
    lfo3.start();

    if (type === 'bandpass' && freq === 1500) {
      const lfo4 = this.ctx.createOscillator();
      lfo4.type = 'sine';
      lfo4.frequency.value = 0.01 + Math.random() * 0.04;

      const lfo4Gain = this.ctx.createGain();
      lfo4Gain.gain.value = vol * (0.2 + Math.random() * 0.2);

      lfo4.connect(lfo4Gain);
      lfo4Gain.connect(gain.gain);
      lfo4.start();
    }

    src.connect(filter).connect(gain).connect(soundGain).connect(this.masterGain!);
    src.start();

    this.nodes.push({ src, filter, gain, soundGain, baseVol: vol });
  },

  setVolume(val: number) {
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(val, this.ctx!.currentTime, 0.5);
    }
  },

  setIntensity(intensity: number) {
    if (!this.nodes.length) return;

    this.nodes[0].filter.frequency.setTargetAtTime(200 + intensity * 400, this.ctx!.currentTime, 3);
    this.nodes[0].gain.gain.setTargetAtTime(this.nodes[0].baseVol * (0.5 + intensity), this.ctx!.currentTime, 3);
    this.nodes[1].filter.frequency.setTargetAtTime(600 + intensity * 600, this.ctx!.currentTime, 3);

    if (this.nodes[3]) {
      this.nodes[3].gain.gain.setTargetAtTime(
        intensity > 0.5 ? this.nodes[3].baseVol * intensity : 0,
        this.ctx!.currentTime,
        3
      );
    }
  },

  setSoundLevel(soundType: SoundType, level: number) {
    if (this.soundGains[soundType]) {
      this.soundGains[soundType].gain.setTargetAtTime(level, this.ctx!.currentTime, 0.5);
    }
  },

  playThunder() {
    if (!this.ctx) return;

    const duration = 1.5 + Math.random() * 1.0;
    const sampleRate = this.ctx.sampleRate;
    const bufferSize = duration * sampleRate;
    const buffer = this.ctx.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      const t = i / sampleRate;
      const envelope = Math.exp(-t * 2) * (1 - Math.exp(-t * 20));
      const noise = Math.random() * 2 - 1;
      const rumble = Math.sin(t * 30) * 0.3 + Math.sin(t * 60) * 0.2;
      data[i] = (noise * 0.5 + rumble) * envelope;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    filter.Q.value = 1;

    const gain = this.ctx.createGain();
    gain.gain.value = 0.8;

    source.connect(filter).connect(gain).connect(this.masterGain!);
    source.start();
  },

  createBirdLayer() {
    if (!this.ctx) return;

    const soundGain = this.ctx.createGain();
    soundGain.gain.value = 0;
    soundGain.connect(this.masterGain!);
    this.soundGains['birds'] = soundGain;

    const bufferSize = 4096;
    const scriptNode = this.ctx.createScriptProcessor(bufferSize, 0, 1);
    let chirpPhase = 0;
    let chirpActive = false;
    let chirpFreq = 2000;
    let nextChirpTime = this.ctx.currentTime + Math.random() * 3;

    scriptNode.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        const t = this.ctx!.currentTime + i / this.ctx!.sampleRate;

        if (t >= nextChirpTime && !chirpActive) {
          chirpActive = true;
          chirpPhase = 0;
          chirpFreq = 1500 + Math.random() * 2000;
          nextChirpTime = t + 1 + Math.random() * 4;
        }

        if (chirpActive) {
          const chirpDuration = 0.05 + Math.random() * 0.1;
          const chirpEnv = Math.max(0, 1 - chirpPhase / (chirpDuration * this.ctx!.sampleRate));
          const freqMod = chirpFreq * (1 + 0.3 * Math.sin(chirpPhase * 0.01));
          const sample = Math.sin((2 * Math.PI * freqMod * chirpPhase) / this.ctx!.sampleRate) * chirpEnv * 0.3;

          output[i] = sample;
          chirpPhase++;

          if (chirpPhase > chirpDuration * this.ctx!.sampleRate) {
            chirpActive = false;
          }
        } else {
          output[i] = 0;
        }
      }
    };

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2500;
    filter.Q.value = 2;

    scriptNode.connect(filter).connect(soundGain);
    this.nodes.push({ src: scriptNode, filter, gain: soundGain, soundGain, baseVol: 0.3 });
  },

  createFireLayer() {
    if (!this.ctx) return;

    const soundGain = this.ctx.createGain();
    soundGain.gain.value = 0;
    soundGain.connect(this.masterGain!);
    this.soundGains['fire'] = soundGain;

    let fireLast = 0;
    const rumbleBuffer = makeLoopBuffer(this.ctx, 2, () => {
      const white = Math.random() * 2 - 1;
      fireLast = (fireLast + 0.01 * white) / 1.01;
      return fireLast * 2;
    });

    const rumbleSrc = this.ctx.createBufferSource();
    rumbleSrc.buffer = rumbleBuffer;
    rumbleSrc.loop = true;

    const rumbleFilter = this.ctx.createBiquadFilter();
    rumbleFilter.type = 'lowpass';
    rumbleFilter.frequency.value = 200;

    const rumbleGain = this.ctx.createGain();
    rumbleGain.gain.value = 0.4;

    rumbleSrc.connect(rumbleFilter).connect(rumbleGain).connect(soundGain);
    rumbleSrc.start();

    const crackleNode = this.ctx.createScriptProcessor(4096, 0, 1);
    let cracklePhase = 0;
    let crackleActive = false;
    let crackleDecay = 0;
    let nextCrackleTime = this.ctx.currentTime + Math.random() * 0.5;

    crackleNode.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);
      for (let i = 0; i < 4096; i++) {
        const t = this.ctx!.currentTime + i / this.ctx!.sampleRate;

        if (t >= nextCrackleTime && !crackleActive) {
          crackleActive = true;
          cracklePhase = 0;
          crackleDecay = 0.02 + Math.random() * 0.05;
          nextCrackleTime = t + 0.1 + Math.random() * 0.8;
        }

        if (crackleActive) {
          const env = Math.exp(-cracklePhase / (crackleDecay * this.ctx!.sampleRate));
          const noise = (Math.random() * 2 - 1) * env * 0.6;
          output[i] = noise;
          cracklePhase++;
          if (env < 0.01) crackleActive = false;
        } else {
          output[i] = 0;
        }
      }
    };

    const crackleFilter = this.ctx.createBiquadFilter();
    crackleFilter.type = 'highpass';
    crackleFilter.frequency.value = 800;

    crackleNode.connect(crackleFilter).connect(soundGain);
    this.nodes.push({ src: rumbleSrc, filter: rumbleFilter, gain: rumbleGain, soundGain, baseVol: 0.4 });
  },

  createStreamLayer() {
    if (!this.ctx) return;

    const soundGain = this.ctx.createGain();
    soundGain.gain.value = 0;
    soundGain.connect(this.masterGain!);
    this.soundGains['stream'] = soundGain;

    // pink noise（Paul Kellet 算法），用 helper 生成无缝循环，滤波器状态在闭包内保持
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    const buffer = makeLoopBuffer(this.ctx, 2, () => {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      const out = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
      return out;
    });

    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1200;
    filter.Q.value = 0.5;

    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.1;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 300;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();

    const gain = this.ctx.createGain();
    gain.gain.value = 0.5;

    src.connect(filter).connect(gain).connect(soundGain);
    src.start();

    this.nodes.push({ src, filter, gain, soundGain, baseVol: 0.5 });
  },

  createWindChimeLayer() {
    if (!this.ctx) return;

    const soundGain = this.ctx.createGain();
    soundGain.gain.value = 0;
    soundGain.connect(this.masterGain!);
    this.soundGains['windchime'] = soundGain;

    const notes = [523.25, 587.33, 659.25, 783.99, 880.00, 1046.50];

    const playChime = () => {
      if (this.soundGains['windchime'].gain.value < 0.01) {
        setTimeout(playChime, 1000);
        return;
      }

      const freq = notes[Math.floor(Math.random() * notes.length)];
      const duration = 0.8 + Math.random() * 1.2;

      const osc = this.ctx!.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const osc2 = this.ctx!.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 2;

      const gain = this.ctx!.createGain();
      const gain2 = this.ctx!.createGain();

      const now = this.ctx!.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      gain2.gain.setValueAtTime(0, now);
      gain2.gain.linearRampToValueAtTime(0.05, now + 0.01);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.7);

      osc.connect(gain).connect(soundGain);
      osc2.connect(gain2).connect(soundGain);

      osc.start(now);
      osc2.start(now);
      osc.stop(now + duration);
      osc2.stop(now + duration);

      const nextDelay = 500 + Math.random() * 3000;
      setTimeout(playChime, nextDelay);
    };

    setTimeout(playChime, 1000);
  },

  initAmbientLayers() {
    this.createBirdLayer();
    this.createFireLayer();
    this.createStreamLayer();
    this.createWindChimeLayer();
  },

  resume() {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  },
};
